import { Link, Outlet, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu, Search, ShieldCheck, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
      <div className="container-app px-6 md:px-10 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-full bg-[#638971] grid place-items-center text-white font-bold">
            <ShieldCheck size={20} strokeWidth={2.4}/>
          </div>
          <span className="font-heading text-xl font-bold text-[#2C4035]">B1 Medical Supply</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#2C4035]">
          <Link to="/" data-testid="nav-home" className="hover:text-[#638971] transition-colors">Home</Link>
          <Link to="/products" data-testid="nav-shop" className="hover:text-[#638971] transition-colors">Shop</Link>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hover:text-[#638971] transition-colors">Admin</Link>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link to="/cart" data-testid="cart-link" className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ShoppingCart size={20} className="text-[#2C4035]" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E48967] text-white text-[11px] font-bold grid place-items-center">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                  <User size={20} className="text-[#2C4035]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel>Hi, {user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/orders")}>My Orders</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>Admin Dashboard</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>
                  <LogOut size={16} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="rounded-full">Login</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link" className="hidden sm:inline">
                <Button className="rounded-full bg-[#638971] hover:bg-[#4F705B] text-white">Get Started</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#2C4035] text-white/90 mt-20">
    <div className="container-app px-6 md:px-10 py-12 grid md:grid-cols-4 gap-8">
      <div>
        <div className="font-heading text-xl font-bold text-white mb-3">B1 Medical Supply</div>
        <p className="text-sm text-white/70 max-w-xs">Trusted medical supplies for home care, wellness and everyday health.</p>
        <a
          href="https://b1medicalsupply.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-site-link"
          className="mt-3 inline-block text-sm text-[#E48967] hover:text-white underline underline-offset-4"
        >
          b1medicalsupply.com
        </a>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3 text-white">Shop</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li><Link to="/products" className="hover:text-white">All Products</Link></li>
          <li><Link to="/products?category=Diagnostics" className="hover:text-white">Diagnostics</Link></li>
          <li><Link to="/products?category=First Aid" className="hover:text-white">First Aid</Link></li>
          <li><Link to="/products?category=PPE" className="hover:text-white">PPE</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3 text-white">Support</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li>Shipping & Returns</li>
          <li>
            <a
              data-testid="footer-contact-email"
              href="mailto:1brandnewproduction@gmail.com"
              className="hover:text-white"
            >
              1brandnewproduction@gmail.com
            </a>
          </li>
          <li>FAQs</li>
        </ul>
      </div>
      <div>
        <div className="text-sm font-semibold mb-3 text-white">Trust</div>
        <p className="text-sm text-white/70">FDA-compliant sourcing. Secure checkout via Stripe. HIPAA-aware handling.</p>
        <p className="mt-3 text-sm">
          Questions?{" "}
          <a
            data-testid="trust-contact-email"
            href="mailto:1brandnewproduction@gmail.com"
            className="text-[#E48967] hover:text-white underline underline-offset-4"
          >
            1brandnewproduction@gmail.com
          </a>
        </p>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-white/60">
      © 2026 B1 Medical Supply. All rights reserved.
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
